import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { callClaude } from '../utils/claudeProxy';
import {
  collection, getDocs, addDoc, doc, deleteDoc, query,
  orderBy, runTransaction, updateDoc, where
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, Tooltip, Divider,
  Alert, LinearProgress, FormControlLabel, Checkbox
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useQRLogo } from '../hooks/useQRLogo';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BarChartIcon from '@mui/icons-material/BarChart';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import QrCodeIcon from '@mui/icons-material/QrCode';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckIcon from '@mui/icons-material/Check';
import ForumIcon from '@mui/icons-material/Forum';
import { deptCode, deptLabel } from '../utils/depts';
import mammoth from 'mammoth';

/* ── QR-dialog ────────────────────────────────────────────────────────────── */
function OfferQRDialog({ offer, open, onClose }) {
  const qrLogo = useQRLogo();
  const svgWrapRef = useRef();
  const [qrCopied, setQrCopied] = useState(false);
  if (!offer) return null;
  const url = `${window.location.origin}/offer/${offer.shareToken}`;
  const QR_SIZE = 260;
  const LOGO_SIZE = 40;

  async function handleCopyQR() {
    const svgEl = svgWrapRef.current?.querySelector('svg');
    if (!svgEl) return;
    try {
      const svgClone = svgEl.cloneNode(true);
      svgClone.querySelectorAll('image').forEach(el => el.remove());
      const svgData = new XMLSerializer().serializeToString(svgClone);
      const canvas = document.createElement('canvas');
      canvas.width = QR_SIZE; canvas.height = QR_SIZE;
      const ctx = canvas.getContext('2d');
      const qrBlob = new Blob([svgData], { type: 'image/svg+xml' });
      const qrBlobUrl = URL.createObjectURL(qrBlob);
      await new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => {
          ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, QR_SIZE, QR_SIZE);
          ctx.drawImage(img, 0, 0, QR_SIZE, QR_SIZE);
          URL.revokeObjectURL(qrBlobUrl); resolve();
        };
        img.onerror = reject; img.src = qrBlobUrl;
      });
      if (qrLogo) {
        await new Promise(resolve => {
          const logoImg = new window.Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.onload = () => {
            const cx = QR_SIZE / 2; const cy = QR_SIZE / 2; const pad = 5;
            ctx.fillStyle = '#fff';
            ctx.fillRect(cx - LOGO_SIZE/2 - pad, cy - LOGO_SIZE/2 - pad, LOGO_SIZE + pad*2, LOGO_SIZE + pad*2);
            ctx.drawImage(logoImg, cx - LOGO_SIZE/2, cy - LOGO_SIZE/2, LOGO_SIZE, LOGO_SIZE);
            resolve();
          };
          logoImg.onerror = resolve; logoImg.src = qrLogo;
        });
      }
      await new Promise((resolve, reject) => {
        canvas.toBlob(async (pngBlob) => {
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
            setQrCopied(true); setTimeout(() => setQrCopied(false), 2000); resolve();
          } catch (e) { reject(e); }
        }, 'image/png');
      });
    } catch {
      await navigator.clipboard.writeText(url);
      setQrCopied(true); setTimeout(() => setQrCopied(false), 2000);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
        <Typography fontWeight={700} textAlign="center">{offer.title || 'Namnlös offert'}</Typography>
        <Box ref={svgWrapRef} sx={{ bgcolor: '#fff', p: 2.5, borderRadius: 2, display: 'inline-flex' }}>
          <QRCodeSVG value={url} size={QR_SIZE} level="H"
            imageSettings={qrLogo ? { src: qrLogo, width: LOGO_SIZE, height: LOGO_SIZE, excavate: true } : undefined} />
        </Box>
        <Typography variant="caption" color="text.secondary"
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'center' }}>
          {url}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button variant="contained" size="small"
            startIcon={qrCopied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
            onClick={handleCopyQR}
            sx={{ textTransform: 'none', bgcolor: qrCopied ? 'success.main' : '#1a1a1a', '&:hover': { bgcolor: qrCopied ? 'success.dark' : '#333' } }}>
            {qrCopied ? 'Kopierad!' : 'Kopiera QR-bild'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} sx={{ textTransform: 'none' }}>Stäng</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ── Mail-dialog ──────────────────────────────────────────────────────────── */
function OfferMailDialog({ offer, open, onClose }) {
  const [recipient, setRecipient] = useState('');
  const [body, setBody] = useState('');
  useEffect(() => {
    if (!offer) return;
    setRecipient(offer.clientEmail || '');
    const url = `${window.location.origin}/offer/${offer.shareToken}`;
    setBody(`Hej,\n\nBifogar vår offert gällande ${offer.title || 'uppdraget'}.\n\nDu hittar offerten här:\n${url}\n\nDu kan godkänna offerten eller lämna synpunkter direkt i länken.\n\nMed vänliga hälsningar`);
  }, [offer]);
  if (!offer) return null;
  const subject = encodeURIComponent(`Offert: ${offer.title || ''}`);
  const mailto = `mailto:${recipient}?subject=${subject}&body=${encodeURIComponent(body)}`;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <EmailIcon sx={{ color: '#1565C0' }} />
        Dela via mail
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <TextField label="Mottagare" size="small" fullWidth value={recipient}
          onChange={e => setRecipient(e.target.value)} />
        <TextField label="Meddelande" size="small" fullWidth multiline rows={7}
          value={body} onChange={e => setBody(e.target.value)} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" href={mailto}
          onClick={onClose}
          sx={{ bgcolor: '#1565C0', '&:hover': { bgcolor: '#0d47a1' }, textTransform: 'none' }}>
          Öppna i mail-klient
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ── Statistik-dialog ─────────────────────────────────────────────────────── */
function OfferStatsDialog({ offer, open, onClose }) {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !offer?.id) return;
    setLoading(true);
    getDocs(query(collection(db, 'offerViews'), where('offerId', '==', offer.id)))
      .then(snap => setViews(snap.docs.map(d => d.data())))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, offer?.id]);
  if (!offer) return null;
  const total = views.length;
  const durViews = views.filter(v => v.duration > 0);
  const avgDur = durViews.length ? Math.round(durViews.reduce((s, v) => s + v.duration, 0) / durViews.length) : 0;
  const fmt = s => s < 60 ? `${s}s` : `${Math.floor(s/60)}m ${s%60}s`;
  const lastView = views.filter(v => v.openedAt).sort((a, b) => b.openedAt?.toMillis?.() - a.openedAt?.toMillis?.())[0];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BarChartIcon sx={{ color: '#FF6B00' }} />
          <Box>
            <Typography fontWeight={700}>{offer.title || 'Namnlös offert'}</Typography>
            <Typography variant="caption" color="text.secondary">Visningsstatistik</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 2, mb: 2 }}>
            {[
              { label: 'Totalt visningar', value: total },
              { label: 'Snitt visningstid', value: avgDur ? fmt(avgDur) : '–' },
              { label: 'Senast öppnad', value: lastView?.openedAt?.toDate?.()?.toLocaleDateString('sv-SE') || '–' },
            ].map(({ label, value }) => (
              <Paper key={label} variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#FF6B00' }}>{value}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Paper>
            ))}
          </Box>
        )}
        {!loading && total === 0 && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            Inga visningar ännu. Dela länken för att börja samla statistik.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
}

const DEPT_COLORS = ['#1565C0', '#2E7D32', '#7B1FA2', '#C62828', '#00695C', '#E65100', '#4527A0', '#0277BD', '#558B2F'];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const expiry = new Date(dateStr); expiry.setHours(0, 0, 0, 0);
  return Math.round((expiry - today) / 86400000);
}

function ExpiryCountdown({ validUntil }) {
  const days = daysUntil(validUntil);
  if (days === null) return null;

  let label, color, bgcolor;
  if (days > 14)       { label = `${days} dagar kvar`; color = '#1B5E20'; bgcolor = '#E8F5E9'; }
  else if (days > 6)   { label = `${days} dagar kvar`; color = '#E65100'; bgcolor = '#FFF3E0'; }
  else if (days > 0)   { label = `${days} dag${days === 1 ? '' : 'ar'} kvar`; color = '#B71C1C'; bgcolor = '#FFEBEE'; }
  else if (days === 0) { label = 'Löper ut idag'; color = '#B71C1C'; bgcolor = '#FFEBEE'; }
  else                 { label = 'Utgången'; color = '#757575'; bgcolor = '#F5F5F5'; }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4,
      px: 0.8, py: 0.15, borderRadius: 1, bgcolor, flexShrink: 0 }}>
      <HourglassBottomIcon sx={{ fontSize: 10, color }} />
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color, lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );
}

// Generates the next offer number for a department atomically.
// Format: {CODE}-{YY}-{NNN}  e.g. BTM-26-001
// Counter doc: offerCounters/{deptId}_{year}  { count: N }
async function generateOfferNumber(deptIds, departments) {
  const yy = String(new Date().getFullYear()).slice(-2);
  const year = new Date().getFullYear();

  // Find first dept with an offerCode configured
  const dept = departments.find(d => deptIds?.includes(d.id) && d.offerCode);
  if (!dept) return '';

  const counterId = `${dept.id}_${year}`;
  const counterRef = doc(db, 'offerCounters', counterId);

  const newCount = await runTransaction(db, async tx => {
    const snap = await tx.get(counterRef);
    const startAt = dept.offerStartNumber || 1;
    const current = snap.exists() ? snap.data().count : startAt - 1;
    const next = current + 1;
    tx.set(counterRef, { count: next, deptId: dept.id, year });
    return next;
  });

  return `${dept.offerCode}-${yy}-${String(newCount).padStart(3, '0')}`;
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function buildSubcontractorPrompt(markupPct) {
  const markup = 1 + markupPct / 100;
  return `Du analyserar en offert från en underleverantör till Tikab Strukturmekanik AB.
Tikab ska skapa sin egen offert till sin kund baserat på underleverantörens offert.
Tikab lägger på ett påslag på ${markupPct}% på alla priser (multiplicera med ${markup}).

Extrahera informationen och returnera ENBART giltig JSON utan markdown-formatering:
{
  "title": "Kort beskrivande offerttitel (på svenska, t.ex. 'Laserskanning Kv. Björnen')",
  "clientCompanyName": "Kundens företagsnamn (den som Tikab ska sälja till)",
  "clientContact": "Kontaktpersonens namn hos kunden",
  "clientEmail": "Kontaktpersonens e-post",
  "clientPhone": "Kontaktpersonens telefonnummer",
  "offerNumber": "Underleverantörens offertnummer (används som referens)",
  "validUntil": "Giltig t.o.m. datum YYYY-MM-DD (tom sträng om ej angivet)",
  "estimatedValue": 0,
  "sections": [
    {
      "type": "intro",
      "title": "Inledning",
      "content": "Kortfattad inledning skriven som om Tikab skriver till sin kund. Nämn uppdraget och platsen.",
      "images": [],
      "imageLayout": "2col"
    },
    {
      "type": "scope",
      "title": "Omfattning",
      "content": "Beskriv vad uppdraget omfattar. Inkludera mätområden, objekt, metoder etc.",
      "images": [],
      "imageLayout": "2col"
    },
    {
      "type": "delivery",
      "title": "Leverans",
      "content": "Vad levereras, i vilket format och när.",
      "images": [],
      "imageLayout": "2col"
    },
    {
      "type": "pricing",
      "title": "Arvoden och villkor",
      "pricingMode": "fixed",
      "rows": [
        {
          "id": "1",
          "description": "Radens benämning (t.ex. 'Laserskanning')",
          "price": "Priset med ${markupPct}% påslag, avrunda till närmaste 100 kr, som sträng",
          "unit": "st eller tim",
          "note": "Eventuell kommentar"
        }
      ],
      "budgetNote": "Eventuell notering om budget eller tillägg",
      "paymentTerms": "Betalningsvillkor extraherat från offerten, eller 'Månadsvis fakturering med redovisning av dagbok och nedlagd tid.' om ej angivet"
    },
    {
      "type": "terms",
      "title": "Övrigt & Villkor",
      "content": "Reservationer, förutsättningar och övriga villkor. Omformulera som Tikabs villkor."
    }
  ]
}

Viktigt:
- estimatedValue ska vara summan av alla priser i pricing-sektionen (med påslag), som ett heltal
- Alla priser ska redan ha ${markupPct}% påslag inräknat
- Skriv innehållet på svenska
- Om ett fält saknas, använd tom sträng eller rimligt standardvärde
- Returnera BARA JSON, inga förklaringar eller markdown`;
}

function parseAiJson(text) {
  // Strip markdown code fences if present
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  const raw = fenced ? fenced[1] : text;
  const match = raw.match(/\{[\s\S]*\}/);
  if (!match) throw new Error('Kunde inte tolka AI-svaret.');
  return JSON.parse(match[0]);
}

async function analyzeSubcontractorFile(file, markupPct) {
  const isPdf  = /\.pdf$/i.test(file.name);
  const isWord = /\.docx?$/i.test(file.name);
  const prompt = buildSubcontractorPrompt(markupPct);

  let messages;

  if (isPdf) {
    const base64 = await fileToBase64(file);
    messages = [{
      role: 'user',
      content: [
        { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
        { type: 'text', text: prompt }
      ]
    }];
  } else if (isWord) {
    const arrayBuffer = await file.arrayBuffer();
    const extracted = await mammoth.extractRawText({ arrayBuffer });
    const docText = extracted.value || '';
    if (!docText.trim()) throw new Error('Kunde inte läsa textinnehåll från Word-filen.');
    messages = [{
      role: 'user',
      content: `${prompt}\n\nHär är offertens textinnehåll:\n\n${docText}`
    }];
  } else {
    throw new Error('Filformat stöds inte. Välj en PDF- eller Word-fil (.docx).');
  }

  const json = await callClaude({ model: 'claude-sonnet-4-6', max_tokens: 4000, messages });
  const text = json.content?.[0]?.text || '';
  return parseAiJson(text);
}

const STATUS_META = {
  draft:    { label: 'Utkast',    color: '#78909C', bg: '#ECEFF1' },
  sent:     { label: 'Skickad',   color: '#1565C0', bg: '#E3F2FD' },
  accepted: { label: 'Accepterad',color: '#2E7D32', bg: '#E8F5E9' },
  declined: { label: 'Nekad',     color: '#C62828', bg: '#FFEBEE' },
  expired:  { label: 'Utgången',  color: '#795548', bg: '#EFEBE9' },
};

const OFFER_TYPES = [
  { key: 'konsult-team', label: 'Konsultteam' },
  { key: 'konsult-cv',   label: 'Konsult-CV' },
  { key: 'fastpris',     label: 'Fastpris' },
  { key: 'lopande',      label: 'Löpande arvode' },
  { key: 'produkt',      label: 'Produkt/SaaS' },
  { key: 'kombination',  label: 'Kombination' },
];

function formatSEK(val) {
  if (!val) return null;
  return Number(val).toLocaleString('sv-SE') + ' kr';
}

const EMPTY_FORM = {
  title: '', templateId: '', companyId: '', type: 'konsult-team',
  departmentIds: [], ownerId: '', estimatedValue: '', validUntil: '',
  description: '', isTest: false,
};

export default function Offers() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [offers, setOffers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [newCompanyDialog, setNewCompanyDialog] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [qrOffer,    setQrOffer]    = useState(null);
  const [mailOffer,  setMailOffer]  = useState(null);
  const [statsOffer, setStatsOffer] = useState(null);
  const [copied,     setCopied]     = useState('');

  // AI import state
  const [importDialog, setImportDialog] = useState(false);
  const [importStep, setImportStep] = useState('upload'); // upload | analyzing | review
  const [importFile, setImportFile] = useState(null);
  const [importMarkup, setImportMarkup] = useState(10);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');
  const [importSaving, setImportSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [offSnap, tmplSnap, compSnap, deptSnap, userSnap] = await Promise.all([
      getDocs(query(collection(db, 'offers'), orderBy('createdAt', 'desc'))),
      getDocs(query(collection(db, 'offerTemplates'), orderBy('name'))),
      getDocs(query(collection(db, 'crmCompanies'), orderBy('name'))),
      getDocs(collection(db, 'departments')),
      getDocs(collection(db, 'users')),
    ]);
    setOffers(offSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setTemplates(tmplSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setCompanies(compSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (searchParams.get('new') === '1') setDialog(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCreate() {
    if (!form.title.trim()) return;
    setSaving(true);

    // Copy sections from template if selected
    let sections = [];
    if (form.templateId) {
      const tmpl = templates.find(t => t.id === form.templateId);
      sections = (tmpl?.sections || []).map(s => ({
        ...s,
        content: s.defaultContent || '',
      }));
    }

    const offerNumber = form.isTest
      ? ''
      : await generateOfferNumber(form.departmentIds, departments);

    const ref = await addDoc(collection(db, 'offers'), {
      ...form,
      offerNumber,
      estimatedValue: form.estimatedValue ? Number(form.estimatedValue) : null,
      sections,
      status: 'draft',
      ownerId: form.ownerId || currentUser.uid,
      createdBy: currentUser.uid,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    setSaving(false);
    setDialog(false);
    navigate(`/offers/${ref.id}`);
  }

  async function handleCreateCompany() {
    if (!newCompanyName.trim()) return;
    const docRef = await addDoc(collection(db, 'crmCompanies'), {
      name: newCompanyName.trim(), createdAt: new Date(),
    });
    const newComp = { id: docRef.id, name: newCompanyName.trim() };
    setCompanies(prev => [...prev, newComp].sort((a, b) => a.name.localeCompare(b.name)));
    setForm(f => ({ ...f, companyId: docRef.id }));
    setNewCompanyName('');
    setNewCompanyDialog(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Ta bort offerten?')) return;
    await deleteDoc(doc(db, 'offers', id));
    setOffers(prev => prev.filter(o => o.id !== id));
  }

  async function handleDuplicate(offer, e) {
    e.stopPropagation();
    const { id: _id, ...data } = offer;
    const ref = await addDoc(collection(db, 'offers'), {
      ...data,
      title: data.title + ' (kopia)',
      status: 'draft',
      offerNumber: '',
      shareEnabled: false,
      shareToken: null,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: currentUser.uid,
    });
    navigate(`/offers/${ref.id}`);
  }

  async function ensureShareToken(offer) {
    if (offer.shareToken) return offer;
    const token = crypto.randomUUID();
    await updateDoc(doc(db, 'offers', offer.id), { shareToken: token, shareEnabled: true });
    const updated = { ...offer, shareToken: token, shareEnabled: true };
    setOffers(prev => prev.map(o => o.id === offer.id ? updated : o));
    return updated;
  }

  async function handleQR(offer, e) {
    e.stopPropagation();
    const o = await ensureShareToken(offer);
    setQrOffer(o);
  }

  async function handleMail(offer, e) {
    e.stopPropagation();
    const o = await ensureShareToken(offer);
    setMailOffer(o);
  }

  async function handleCopyLink(offer, e) {
    e.stopPropagation();
    const o = await ensureShareToken(offer);
    await navigator.clipboard.writeText(`${window.location.origin}/offer/${o.shareToken}`);
    setCopied(offer.id);
    setTimeout(() => setCopied(''), 2000);
  }

  function openImportDialog() {
    setImportStep('upload');
    setImportFile(null);
    setImportMarkup(10);
    setImportResult(null);
    setImportError('');
    setImportDialog(true);
  }

  async function handleAnalyze() {
    if (!importFile) return;
    setImportStep('analyzing');
    setImportError('');
    try {
      const result = await analyzeSubcontractorFile(importFile, importMarkup);
      setImportResult(result);
      setImportStep('review');
    } catch (err) {
      console.error(err);
      setImportError(err.message || 'Analys misslyckades.');
      setImportStep('upload');
    }
  }

  // Recalculate prices when markup changes in review step
  function handleMarkupChange(newMarkup) {
    const num = parseFloat(newMarkup);
    if (isNaN(num) || !importResult) { setImportMarkup(newMarkup); return; }
    const oldFactor = 1 + importMarkup / 100;
    const newFactor = 1 + num / 100;
    // Back-calculate original price, apply new markup
    const updatedSections = importResult.sections.map(s => {
      if (s.type !== 'pricing') return s;
      return {
        ...s,
        rows: s.rows.map(r => {
          const original = parseFloat(r.price) / oldFactor;
          const newPrice = Math.round(original * newFactor / 100) * 100;
          return { ...r, price: String(newPrice) };
        }),
      };
    });
    const newTotal = updatedSections
      .find(s => s.type === 'pricing')
      ?.rows.reduce((sum, r) => sum + (parseFloat(r.price) || 0), 0) || 0;
    setImportResult(prev => ({
      ...prev,
      sections: updatedSections,
      estimatedValue: newTotal,
    }));
    setImportMarkup(num);
  }

  async function handleImportCreate() {
    if (!importResult) return;
    setImportSaving(true);
    try {
      // Find or create company
      let companyId = '';
      if (importResult.clientCompanyName) {
        const existing = companies.find(
          c => c.name.toLowerCase() === importResult.clientCompanyName.toLowerCase()
        );
        if (existing) {
          companyId = existing.id;
        } else {
          const docRef = await addDoc(collection(db, 'crmCompanies'), {
            name: importResult.clientCompanyName,
            createdAt: new Date(),
          });
          companyId = docRef.id;
          setCompanies(prev =>
            [...prev, { id: docRef.id, name: importResult.clientCompanyName }]
              .sort((a, b) => a.name.localeCompare(b.name))
          );
        }
      }

      const sections = (importResult.sections || []).map(s => ({
        ...s,
        id: Date.now().toString() + Math.random(),
      }));

      const offerNumber = await generateOfferNumber([], departments);

      const ref = await addDoc(collection(db, 'offers'), {
        title: importResult.title || 'Importerad offert',
        type: 'fastpris',
        companyId,
        clientContact: importResult.clientContact || '',
        clientEmail: importResult.clientEmail || '',
        clientPhone: importResult.clientPhone || '',
        ourContact: importResult.ourContact || '',
        offerNumber,
        validUntil: importResult.validUntil || '',
        estimatedValue: importResult.estimatedValue || null,
        departmentIds: [],
        sections,
        status: 'draft',
        ownerId: currentUser.uid,
        createdBy: currentUser.uid,
        importedFrom: importFile.name,
        markupPct: importMarkup,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setImportDialog(false);
      navigate(`/offers/${ref.id}`);
    } catch (err) {
      console.error(err);
      setImportError('Kunde inte skapa offerten.');
    } finally {
      setImportSaving(false);
    }
  }

  const filtered = offers.filter(o => {
    const q = search.toLowerCase();
    const company = companies.find(c => c.id === o.companyId);
    const matchSearch = !q ||
      o.title?.toLowerCase().includes(q) ||
      company?.name?.toLowerCase().includes(q);
    const matchStatus = !filterStatus || o.status === filterStatus;
    const matchDept = !filterDept || (o.departmentIds || []).includes(filterDept);
    return matchSearch && matchStatus && matchDept;
  });

  // Stats
  const draftCount = offers.filter(o => o.status === 'draft').length;
  const sentCount = offers.filter(o => o.status === 'sent').length;
  const acceptedCount = offers.filter(o => o.status === 'accepted').length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Offerter</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {offers.length} offerter totalt
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<BarChartIcon />}
            onClick={() => navigate('/offers/dashboard')}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', border: '1px solid' }}>
            Översikt
          </Button>
          <Button size="small" startIcon={<ForumIcon />}
            onClick={() => navigate('/offers/aterkoppling')}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', border: '1px solid',
              position: 'relative' }}>
            Återkoppling
            {offers.filter(o => o.clientResponse?.status).length > 0 && (
              <Box sx={{
                position: 'absolute', top: -4, right: -4,
                bgcolor: '#FF6B00', color: 'white', borderRadius: '50%',
                width: 16, height: 16, fontSize: '0.6rem', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {offers.filter(o => o.clientResponse?.status).length}
              </Box>
            )}
          </Button>
          <Button size="small" startIcon={<SettingsIcon />}
            onClick={() => navigate('/offer-templates')}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', border: '1px solid' }}>
            Mallar
          </Button>
          <Button size="small" startIcon={<UploadFileIcon />}
            onClick={openImportDialog}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', border: '1px solid' }}>
            Importera PDF
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setForm({ ...EMPTY_FORM, ownerId: currentUser.uid }); setDialog(true); }}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            Ny offert
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Utkast',    value: draftCount,    color: '#78909C' },
            { label: 'Skickade',  value: sentCount,     color: '#1565C0' },
            { label: 'Accepterade', value: acceptedCount, color: '#2E7D32' },
            { label: 'Totalt',    value: offers.length, color: '#FF6B00' },
          ].map((s, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: s.color }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Sök offert eller företag..."
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            )}} />
          <TextField select size="small" value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)} sx={{ minWidth: 160 }} label="Status">
            <MenuItem value="">Alla statusar</MenuItem>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" value={filterDept}
            onChange={e => setFilterDept(e.target.value)} sx={{ minWidth: 170 }} label="Affärsområde">
            <MenuItem value="">Alla AO</MenuItem>
            {departments.map(d => <MenuItem key={d.id} value={d.id}>{deptLabel(d)}</MenuItem>)}
          </TextField>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {offers.length === 0
                ? 'Inga offerter ännu. Skapa den första!'
                : 'Inga träffar.'}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filtered.map(offer => {
              const st = STATUS_META[offer.status] || STATUS_META.draft;
              const company = companies.find(c => c.id === offer.companyId);
              const owner = users.find(u => u.id === offer.ownerId);
              const tmpl = templates.find(t => t.id === offer.templateId);
              const typeLabel = OFFER_TYPES.find(t => t.key === offer.type)?.label || offer.type;
              const dept = departments.find(d => (offer.departmentIds || []).includes(d.id));
              const deptIdx = dept ? departments.indexOf(dept) : -1;
              const deptColor = deptIdx >= 0 ? DEPT_COLORS[deptIdx % DEPT_COLORS.length] : '#bdbdbd';
              return (
                <Paper key={offer.id} sx={{
                  cursor: 'pointer', overflow: 'hidden',
                  '&:hover': { boxShadow: 3 },
                  display: 'flex', alignItems: 'stretch',
                }}
                  onClick={() => navigate(`/offers/${offer.id}`)}>

                  {/* AO color stripe */}
                  <Box sx={{ width: 6, flexShrink: 0, bgcolor: deptColor }} />

                  {/* Offer number */}
                  <Box sx={{
                    width: 100, flexShrink: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', px: 1,
                    borderRight: '1px solid', borderColor: 'divider',
                  }}>
                    <Typography variant="caption" fontWeight={700}
                      sx={{ color: deptColor, fontSize: '0.7rem', textAlign: 'center', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                      {offer.offerNumber || '–'}
                    </Typography>
                  </Box>

                  {/* Main content */}
                  <Box sx={{ flex: 1, minWidth: 0, p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                      <Typography fontWeight={600} noWrap>{offer.title}</Typography>
                      {dept && (
                        <Chip label={deptCode(dept)} size="small"
                          sx={{ fontSize: '0.6rem', height: 18, bgcolor: deptColor, color: 'white', fontWeight: 700 }} />
                      )}
                      <Chip label={typeLabel} size="small"
                        sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#f5f5f5' }} />
                      {tmpl && (
                        <Chip label={tmpl.name} size="small"
                          sx={{ fontSize: '0.6rem', height: 18 }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {company && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <BusinessIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">{company.name}</Typography>
                        </Box>
                      )}
                      {owner && (
                        <Typography variant="caption" color="text.secondary">
                          {owner.displayName || owner.email}
                        </Typography>
                      )}
                      <ExpiryCountdown validUntil={offer.validUntil} />
                    </Box>
                  </Box>

                  {/* Right: value + status + badges + actions */}
                  <Box sx={{ pr: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {offer.estimatedValue && (
                      <Typography fontWeight={700} sx={{ color: st.color, fontSize: '0.9rem', mr: 0.5 }}>
                        {formatSEK(offer.estimatedValue)}
                      </Typography>
                    )}
                    {offer.clientResponse?.status === 'approved' && (
                      <Tooltip title="Kunden har godkänt offerten">
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#2E7D32', mr: 0.5 }} />
                      </Tooltip>
                    )}
                    {offer.clientResponse?.status === 'commented' && (
                      <Tooltip title={offer.clientResponse.comment || 'Kunden har kommenterat'}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4,
                          px: 0.8, py: 0.2, borderRadius: 1, bgcolor: '#E3F2FD', mr: 0.5 }}>
                          <ChatBubbleIcon sx={{ fontSize: 11, color: '#1565C0' }} />
                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#1565C0', lineHeight: 1 }}>
                            Kommentar
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                    <Chip label={st.label} size="small"
                      sx={{ fontSize: '0.65rem', height: 20, bgcolor: st.bg, color: st.color, fontWeight: 600, mr: 0.5 }} />

                    {/* Action icons — same pattern as PitchN */}
                    <Box sx={{ display: 'flex', gap: 0.5 }} onClick={e => e.stopPropagation()}>
                      <Tooltip title="Öppna">
                        <IconButton size="small" onClick={() => navigate(`/offers/${offer.id}`)}>
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicera">
                        <IconButton size="small" onClick={e => handleDuplicate(offer, e)}
                          sx={{ '&:hover': { color: '#FF6B00' } }}>
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="QR-kod">
                        <IconButton size="small" onClick={e => handleQR(offer, e)}>
                          <QrCodeIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dela via mail">
                        <IconButton size="small" onClick={e => handleMail(offer, e)}
                          sx={{ '&:hover': { color: '#1565C0' } }}>
                          <EmailIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={copied === offer.id ? 'Kopierat!' : 'Kopiera delningslänk'}>
                        <IconButton size="small" onClick={e => handleCopyLink(offer, e)}
                          sx={{ color: copied === offer.id ? 'success.main' : undefined }}>
                          <LinkIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Statistik">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); setStatsOffer(offer); }}>
                          <BarChartIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Radera">
                        <IconButton size="small"
                          onClick={e => { e.stopPropagation(); handleDelete(offer.id); }}
                          sx={{ color: '#bbb', '&:hover': { color: '#d32f2f' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Container>

      {/* New offer dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { minWidth: { sm: 560 } } }}>
        <DialogTitle sx={{ px: 3, py: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DescriptionIcon sx={{ color: '#FF6B00', fontSize: 30 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>Ny offert</Typography>
              <Typography variant="caption" color="text.secondary">
                Fyll i grunduppgifter för att komma igång
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Titel */}
            <TextField fullWidth label="Offerttitel *"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              autoFocus />

            {/* Företag */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField select fullWidth label="Kund / Företag"
                value={form.companyId}
                onChange={e => setForm(f => ({ ...f, companyId: e.target.value }))}>
                <MenuItem value="">– Välj företag –</MenuItem>
                {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
              <Tooltip title="Skapa nytt företag">
                <IconButton onClick={() => setNewCompanyDialog(true)}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, flexShrink: 0 }}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Typ av offert */}
            <TextField select fullWidth label="Typ av offert"
              value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
              {OFFER_TYPES.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
            </TextField>

            {/* Mall */}
            <TextField select fullWidth label="Utgå från mall (valfritt)"
              value={form.templateId}
              onChange={e => {
                const tmpl = templates.find(t => t.id === e.target.value);
                setForm(f => ({
                  ...f,
                  templateId: e.target.value,
                  type: tmpl?.type || f.type,
                  departmentIds: tmpl?.departmentId ? [tmpl.departmentId] : f.departmentIds,
                }));
              }}>
              <MenuItem value="">– Ingen mall –</MenuItem>
              {templates.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                  {t.departmentId && ` · ${departments.find(d => d.id === t.departmentId)?.name || ''}`}
                </MenuItem>
              ))}
            </TextField>

            {/* Ägare */}
            <TextField select fullWidth label="Ansvarig säljare"
              value={form.ownerId}
              onChange={e => setForm(f => ({ ...f, ownerId: e.target.value }))}>
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>
                  {u.displayName || u.email}
                </MenuItem>
              ))}
            </TextField>

            {/* Värde + Giltig t.o.m. */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label="Estimerat värde (SEK)" type="number"
                value={form.estimatedValue}
                onChange={e => setForm(f => ({ ...f, estimatedValue: e.target.value }))} />
              <TextField fullWidth label="Giltig t.o.m."
                type="date" InputLabelProps={{ shrink: true }}
                value={form.validUntil}
                onChange={e => setForm(f => ({ ...f, validUntil: e.target.value }))} />
            </Box>

            {/* Affärsområden — Admin exkluderas */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="bold"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
                Affärsområde
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {departments
                  .filter(d => d.name.toLowerCase() !== 'admin')
                  .map(d => {
                    const sel = (form.departmentIds || []).includes(d.id);
                    return (
                      <Tooltip key={d.id} title={d.name}>
                        <Chip label={deptCode(d)}
                          onClick={() => setForm(f => ({
                            ...f,
                            departmentIds: sel
                              ? f.departmentIds.filter(id => id !== d.id)
                              : [...(f.departmentIds || []), d.id]
                          }))}
                          variant={sel ? 'filled' : 'outlined'}
                          sx={{
                            bgcolor: sel ? '#1a1a1a' : 'transparent',
                            color: sel ? 'white' : 'text.primary',
                            cursor: 'pointer',
                            fontWeight: sel ? 600 : 400,
                          }} />
                      </Tooltip>
                    );
                  })}
              </Box>
            </Box>

          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <FormControlLabel
            sx={{ mr: 'auto' }}
            control={
              <Checkbox
                size="small"
                checked={!!form.isTest}
                onChange={e => setForm(f => ({ ...f, isTest: e.target.checked }))}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Testoffer (inget offertnummer bokförs)
              </Typography>
            }
          />
          <Button onClick={() => setDialog(false)} size="large">Avbryt</Button>
          <Button variant="contained" onClick={handleCreate} size="large"
            disabled={saving || !form.title.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {saving ? 'Skapar...' : 'Skapa offert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: skapa nytt företag */}
      <Dialog open={newCompanyDialog} onClose={() => setNewCompanyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nytt företag</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Företagsnamn" sx={{ mt: 1 }}
            value={newCompanyName}
            onChange={e => setNewCompanyName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreateCompany()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCompanyDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleCreateCompany}
            disabled={!newCompanyName.trim()}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
            Skapa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Importera underleverantörsoffert */}
      <Dialog open={importDialog}
        onClose={() => importStep !== 'analyzing' && setImportDialog(false)}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AutoFixHighIcon sx={{ color: '#FF6B00' }} />
            <Box>
              <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
                Importera underleverantörsoffert
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {importStep === 'upload' && 'Ladda upp PDF eller Word och ange påslag'}
                {importStep === 'analyzing' && 'AI analyserar dokumentet...'}
                {importStep === 'review' && 'Granska och justera'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />

        {importStep === 'upload' && (
          <>
            <DialogContent sx={{ pt: 3 }}>
              {importError && (
                <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>
              )}
              <Box sx={{
                border: '2px dashed',
                borderColor: importFile ? '#FF6B00' : 'divider',
                borderRadius: 2, p: 3, textAlign: 'center',
                cursor: 'pointer', mb: 3,
                bgcolor: importFile ? '#fff8f5' : 'background.paper',
                transition: 'all 0.2s',
              }}
                onClick={() => document.getElementById('import-pdf-input').click()}>
                <input id="import-pdf-input" type="file"
                  accept="application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  style={{ display: 'none' }}
                  onChange={e => setImportFile(e.target.files[0] || null)} />
                <UploadFileIcon sx={{ fontSize: 40, color: importFile ? '#FF6B00' : 'text.disabled', mb: 1 }} />
                {importFile ? (
                  <Typography fontWeight={600} color="#FF6B00">{importFile.name}</Typography>
                ) : (
                  <>
                    <Typography color="text.secondary">
                      Klicka för att välja offert från underleverantör
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      PDF eller Word (.docx)
                    </Typography>
                  </>
                )}
              </Box>

              <TextField
                fullWidth
                label="Påslag (%)"
                type="number"
                value={importMarkup}
                onChange={e => setImportMarkup(e.target.value)}
                helperText="Tikabs påslag på underleverantörens priser. Alla priser räknas om automatiskt."
                inputProps={{ min: 0, max: 200, step: 1 }}
              />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button onClick={() => setImportDialog(false)}>Avbryt</Button>
              <Button variant="contained" startIcon={<AutoFixHighIcon />}
                onClick={handleAnalyze}
                disabled={!importFile}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
                Analysera med AI
              </Button>
            </DialogActions>
          </>
        )}

        {importStep === 'analyzing' && (
          <DialogContent sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#FF6B00', mb: 2 }} />
            <Typography>Läser och analyserar dokument...</Typography>
            <LinearProgress sx={{ mt: 3, borderRadius: 1,
              '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />
          </DialogContent>
        )}

        {importStep === 'review' && importResult && (
          <>
            <DialogContent sx={{ pt: 3 }}>
              <Typography variant="caption" color="text.secondary"
                fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Påslag
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, mt: 0.5 }}>
                <TextField
                  size="small"
                  label="Påslag (%)"
                  type="number"
                  value={importMarkup}
                  onChange={e => handleMarkupChange(e.target.value)}
                  inputProps={{ min: 0, max: 200, step: 1 }}
                  sx={{ width: 130 }}
                  helperText="Ändra för att räkna om priser"
                />
                {importResult.estimatedValue > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Totalt värde</Typography>
                    <Typography fontWeight={700} sx={{ color: '#FF6B00' }}>
                      {Number(importResult.estimatedValue).toLocaleString('sv-SE')} kr
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Offerttitel" size="small"
                    value={importResult.title || ''}
                    onChange={e => setImportResult(p => ({ ...p, title: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Kund / Företag" size="small"
                    value={importResult.clientCompanyName || ''}
                    onChange={e => setImportResult(p => ({ ...p, clientCompanyName: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Kontaktperson" size="small"
                    value={importResult.clientContact || ''}
                    onChange={e => setImportResult(p => ({ ...p, clientContact: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Telefon" size="small"
                    value={importResult.clientPhone || ''}
                    onChange={e => setImportResult(p => ({ ...p, clientPhone: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="E-post" size="small"
                    value={importResult.clientEmail || ''}
                    onChange={e => setImportResult(p => ({ ...p, clientEmail: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Offertnummer (ref)" size="small"
                    value={importResult.offerNumber || ''}
                    onChange={e => setImportResult(p => ({ ...p, offerNumber: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Giltig t.o.m." size="small"
                    type="date" InputLabelProps={{ shrink: true }}
                    value={importResult.validUntil || ''}
                    onChange={e => setImportResult(p => ({ ...p, validUntil: e.target.value }))} />
                </Grid>
              </Grid>

              {/* Pricing preview */}
              {importResult.sections?.find(s => s.type === 'pricing') && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Priser (inkl. {importMarkup}% påslag)
                  </Typography>
                  {importResult.sections.find(s => s.type === 'pricing').rows.map((row, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                      <TextField size="small" label="Benämning" sx={{ flex: 2 }}
                        value={row.description}
                        onChange={e => setImportResult(prev => {
                          const sections = prev.sections.map(s => {
                            if (s.type !== 'pricing') return s;
                            const rows = s.rows.map((r, ri) =>
                              ri === i ? { ...r, description: e.target.value } : r);
                            return { ...s, rows };
                          });
                          return { ...prev, sections };
                        })} />
                      <TextField size="small" label="Pris (kr)" sx={{ flex: 1 }}
                        type="number"
                        value={row.price}
                        onChange={e => {
                          const newPrice = e.target.value;
                          setImportResult(prev => {
                            const sections = prev.sections.map(s => {
                              if (s.type !== 'pricing') return s;
                              const rows = s.rows.map((r, ri) =>
                                ri === i ? { ...r, price: newPrice } : r);
                              return { ...s, rows };
                            });
                            const newTotal = sections
                              .find(s => s.type === 'pricing')
                              ?.rows.reduce((sum, r) => sum + (parseFloat(r.price) || 0), 0) || 0;
                            return { ...prev, sections, estimatedValue: newTotal };
                          });
                        }} />
                      <TextField size="small" label="Enhet" sx={{ width: 80 }}
                        value={row.unit}
                        onChange={e => setImportResult(prev => {
                          const sections = prev.sections.map(s => {
                            if (s.type !== 'pricing') return s;
                            const rows = s.rows.map((r, ri) =>
                              ri === i ? { ...r, unit: e.target.value } : r);
                            return { ...s, rows };
                          });
                          return { ...prev, sections };
                        })} />
                    </Box>
                  ))}
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Du kan redigera allt mer i detalj i offertredigeraren efter att offerten skapats.
              </Typography>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button onClick={() => setImportStep('upload')}>Tillbaka</Button>
              <Button variant="contained"
                onClick={handleImportCreate}
                disabled={importSaving || !importResult.title}
                startIcon={importSaving
                  ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
                {importSaving ? 'Skapar...' : 'Skapa offert'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <OfferQRDialog   offer={qrOffer}    open={!!qrOffer}    onClose={() => setQrOffer(null)} />
      <OfferMailDialog offer={mailOffer}  open={!!mailOffer}  onClose={() => setMailOffer(null)} />
      <OfferStatsDialog offer={statsOffer} open={!!statsOffer} onClose={() => setStatsOffer(null)} />
    </Box>
  );
}
